import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../lib/tokens';
import { TableScroll } from '../../components/TableScroll';
import { fetchArticles } from './api';
import { ArticleEditor } from './ArticleEditor';
import type {
  ArticleListItem,
  ArticleStatus,
  ArticleCluster,
  ArticleCategory,
} from './types';

const STATUS_META: Record<ArticleStatus, { label: string; bg: string; color: string }> = {
  DRAFT: { label: 'Entwurf', bg: darkTheme.colors.bgElevated, color: darkTheme.colors.textSecondary },
  REVIEW: { label: 'Review', bg: darkTheme.colors.warningBg, color: darkTheme.colors.warning },
  SCHEDULED: { label: 'Geplant', bg: darkTheme.colors.infoBg, color: darkTheme.colors.info },
  PUBLISHED: { label: 'Live', bg: darkTheme.colors.successBg, color: darkTheme.colors.success },
  ARCHIVED: { label: 'Archiviert', bg: darkTheme.colors.bgElevated, color: darkTheme.colors.textTertiary },
};

const CLUSTER_LABEL: Record<ArticleCluster, string> = {
  WISSEN: 'Wissen',
  PRUEFUNG: 'Prüfung',
  KARRIERE: 'Karriere',
  BLOG: 'Blog',
};

const CATEGORY_LABEL: Record<ArticleCategory, string> = {
  GLOSSAR: 'Glossar',
  PRUEFUNG: 'Prüfung',
  KARRIERE: 'Karriere',
  RECHT: 'Recht',
  STUDIE: 'Studie',
  NEWS: 'News',
  METHODIK: 'Methodik',
  PRODUKT: 'Produkt',
};

type StatusFilter = ArticleStatus | 'ALL';
type ClusterFilter = ArticleCluster | 'ALL';

export function ArticlesPage(): JSX.Element {
  const [editing, setEditing] = useState<string | null | 'new'>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [clusterFilter, setClusterFilter] = useState<ClusterFilter>('ALL');
  const [search, setSearch] = useState('');

  const query = useQuery({
    queryKey: ['articles', { status: statusFilter, cluster: clusterFilter, q: search }],
    queryFn: () =>
      fetchArticles({
        status: statusFilter === 'ALL' ? undefined : statusFilter,
        cluster: clusterFilter === 'ALL' ? undefined : clusterFilter,
        q: search || undefined,
        limit: 100,
      }),
  });

  const items = query.data?.items ?? [];

  return (
    <div className="mx-auto max-w-[1200px]">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold mb-1">Artikel</h1>
          <p className="text-sm text-text-secondary">
            Blog · Wissen · Prüfung · Karriere — alle Artikel an einem Ort.
          </p>
        </div>
        <button
          onClick={() => setEditing('new')}
          className="px-4 py-2 rounded-lg font-semibold text-white bg-accent hover:bg-accent-hover"
        >
          + Neuer Artikel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 rounded-lg text-sm bg-bg-elevated border border-border-default"
        >
          <option value="ALL">Alle Status</option>
          <option value="DRAFT">Entwurf</option>
          <option value="REVIEW">Review</option>
          <option value="SCHEDULED">Geplant</option>
          <option value="PUBLISHED">Live</option>
          <option value="ARCHIVED">Archiviert</option>
        </select>
        <select
          value={clusterFilter}
          onChange={(e) => setClusterFilter(e.target.value as ClusterFilter)}
          className="px-3 py-2 rounded-lg text-sm bg-bg-elevated border border-border-default"
        >
          <option value="ALL">Alle Cluster</option>
          <option value="WISSEN">Wissen</option>
          <option value="PRUEFUNG">Prüfung</option>
          <option value="KARRIERE">Karriere</option>
          <option value="BLOG">Blog</option>
        </select>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Suche..."
          className="flex-1 min-w-[200px] px-3 py-2 rounded-lg text-sm bg-bg-elevated border border-border-default"
        />
      </div>

      {/* Table */}
      {query.isLoading && <p className="text-sm text-text-tertiary">Lade…</p>}
      {query.isError && (
        <p className="text-sm text-danger">Fehler: {(query.error as Error).message}</p>
      )}
      {!query.isLoading && items.length === 0 && (
        <div className="rounded-xl p-8 text-center bg-bg-surface border border-border-subtle">
          <p className="text-text-secondary mb-3">Noch keine Artikel.</p>
          <button
            onClick={() => setEditing('new')}
            className="px-4 py-2 rounded-lg font-semibold text-white bg-accent hover:bg-accent-hover"
          >
            Ersten Artikel erstellen
          </button>
        </div>
      )}
      {items.length > 0 && (
        <TableScroll
          className="rounded-xl border border-border-subtle bg-bg-surface"
          minWidthClass="min-w-[760px]"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-bg-elevated">
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-text-tertiary">Titel</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-text-tertiary">Status</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-text-tertiary">Cluster</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-text-tertiary">Kategorie</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-text-tertiary">Autor</th>
                <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-text-tertiary">Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((article: ArticleListItem) => {
                const status = STATUS_META[article.status];
                return (
                  <tr
                    key={article.id}
                    onClick={() => setEditing(article.id)}
                    className="cursor-pointer hover:bg-bg-elevated border-t border-border-subtle"
                  >
                    <td className="px-4 py-3.5">
                      <div className="font-semibold text-text-primary">{article.title}</div>
                      <div className="text-[11px] text-text-tertiary font-mono mt-0.5">/{article.slug}</div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className="px-2 py-0.5 rounded-md text-[11px] font-semibold"
                        style={{ background: status.bg, color: status.color }}
                      >
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-text-secondary">{CLUSTER_LABEL[article.cluster]}</td>
                    <td className="px-4 py-3.5 text-text-secondary">{CATEGORY_LABEL[article.category]}</td>
                    <td className="px-4 py-3.5 text-text-secondary">
                      {article.author?.displayName ?? '—'}
                    </td>
                    <td className="px-4 py-3.5 text-text-tertiary text-xs">
                      {new Date(article.updatedAt).toLocaleDateString('de-DE')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}

      {editing !== null && (
        <ArticleEditor
          articleId={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
